/**
 * ONE TeX delimiter model for both clients.
 *
 * Flashcard bodies are authored as TipTap HTML with raw TeX left in the text
 * (`About $40\%$.`), and each platform then renders that text with a different
 * engine — web with KaTeX in the DOM, mobile with MathJax→SVG inside a native
 * `<Text>` run. The ENGINE is allowed to differ; which substrings are math is
 * not, and it had drifted:
 *
 *  - mobile accepted all four families (`$…$`, `\(…\)`, `$$…$$`, `\[…\]`)
 *    because that is what its MathJax `TeX` input jax was configured with;
 *  - web's splitter was a lone `/(\$[^$]+\$)/g`, so `\(x\)` rendered as math on
 *    a phone and as the literal characters in a browser, and `$$x$$` split on
 *    its own delimiters.
 *
 * Both now read this table, so a card can only ever be segmented one way.
 * Kept deliberately free of any renderer type — this module is pure string
 * work and must stay importable from React Native, the browser and the server.
 */

export interface MathDelimiter {
  open: string;
  close: string;
  /** Display (block) math — KaTeX `displayMode`, MathJax `displayMath`. */
  display: boolean;
}

/**
 * Longest-open-first. `$$` MUST be tried before `$`, otherwise a display
 * formula is read as an empty inline one followed by stray text.
 */
export const MATH_DELIMITERS: readonly MathDelimiter[] = [
  { open: '$$', close: '$$', display: true },
  { open: '\\[', close: '\\]', display: true },
  { open: '$', close: '$', display: false },
  { open: '\\(', close: '\\)', display: false },
];

/** `[open, close]` pairs in the shape MathJax's `TeX` input jax expects. */
export const MATHJAX_INLINE_DELIMITERS: [string, string][] = MATH_DELIMITERS.filter(
  (d) => !d.display,
).map((d) => [d.open, d.close]);

export const MATHJAX_DISPLAY_DELIMITERS: [string, string][] = MATH_DELIMITERS.filter(
  (d) => d.display,
).map((d) => [d.open, d.close]);

/**
 * Cheap pre-check: could this string contain math at all?
 *
 * Mobile's TeX→SVG pass is the single most expensive thing on the deck screen
 * and the vast majority of cards are plain text, so the native renderer skips
 * the whole typeset when this is false. Deliberately conservative — any
 * character that could open, close or escape a delimiter sends the content down
 * the full path.
 *
 * `\begin` / `\ref` / `\eqref` are in the probe even though `splitMathSegments` below does
 * NOT treat them as delimiters: MathJax's TeX input jax defaults to `processEnvironments: true`
 * and `processRefs: true`, so a card whose only math is a bare `\begin{pmatrix}…\end{pmatrix}`
 * (no `$`, no `\(`) still typesets on native. Leaving them out made the probe disagree with the
 * engine it gates, and such a card silently rendered as literal backslash text.
 */
const DELIMITER_PROBE = /\$|\\\(|\\\)|\\\[|\\\]|\\begin\b|\\eqref\b|\\ref\b/;

export const containsMathDelimiter = (text: string): boolean =>
  DELIMITER_PROBE.test(text);

export type MathSegment =
  | { type: 'text'; value: string }
  | { type: 'math'; value: string; display: boolean };

/**
 * A math run located in the ORIGINAL string: `[start, end)` spans the delimiters
 * too, `value` is the body between them. Positions let a caller that scans the
 * authored text with its own regex (the MCQ option-marker detector) skip anything
 * that sits inside a formula — `P(A) \leq 1` contains the characters `A) `.
 */
export interface MathRun {
  start: number;
  end: number;
  value: string;
  display: boolean;
}

/**
 * ONE scan of the delimiter table, shared by {@link splitMathSegments} and
 * {@link locateMathRuns} so the two can never disagree on where a formula is.
 *
 * Rules, chosen to match MathJax's `processEscapes: true` (which mobile has
 * always run with) so the two clients agree on the awkward cases:
 *
 *  - `\$` is an ESCAPED dollar: it yields a literal `$` and never opens math.
 *  - an unterminated opener is literal text, not a formula that swallows the
 *    rest of the card — a truncated AI generation must still read as words.
 *  - an empty body (`$$`, `\(\)`) is literal text: there is nothing to typeset.
 */
function scanMath(
  text: string,
  onLiteral: (char: string) => void,
  onMath: (run: MathRun) => void,
) {
  let i = 0;
  while (i < text.length) {
    // `\$` — an escaped dollar is content, not a delimiter.
    if (text[i] === '\\' && text[i + 1] === '$') {
      onLiteral('$');
      i += 2;
      continue;
    }

    const delimiter = MATH_DELIMITERS.find((d) => text.startsWith(d.open, i));
    if (!delimiter) {
      onLiteral(text[i]);
      i += 1;
      continue;
    }

    const bodyStart = i + delimiter.open.length;
    const closeAt = text.indexOf(delimiter.close, bodyStart);
    // Unterminated, or nothing between the delimiters → literal text.
    if (closeAt === -1 || closeAt === bodyStart) {
      onLiteral(text[i]);
      i += 1;
      continue;
    }

    const end = closeAt + delimiter.close.length;
    onMath({
      start: i,
      end,
      value: text.slice(bodyStart, closeAt),
      display: delimiter.display,
    });
    i = end;
  }
}

/**
 * Split authored text into literal runs and math runs.
 *
 * Adjacent literal characters are coalesced, so a caller can render one text
 * node per segment without producing a node per character.
 */
export function splitMathSegments(text: string): MathSegment[] {
  const segments: MathSegment[] = [];
  let literal = '';

  const flushLiteral = () => {
    if (!literal) return;
    segments.push({ type: 'text', value: literal });
    literal = '';
  };

  scanMath(
    text,
    (char) => {
      literal += char;
    },
    (run) => {
      flushLiteral();
      segments.push({ type: 'math', value: run.value, display: run.display });
    },
  );

  flushLiteral();
  return segments;
}

/** Every well-formed formula in `text`, with its position in `text`. */
export function locateMathRuns(text: string): MathRun[] {
  const runs: MathRun[] = [];
  scanMath(
    text,
    () => {},
    (run) => runs.push(run),
  );
  return runs;
}

/**
 * A same-length copy of `text` in which every math run (delimiters included) is
 * overwritten with `fill`, so a regex scanned over the result reports indices
 * that address the ORIGINAL string — and can never match inside a formula.
 *
 * `fill` defaults to NUL rather than a space: a space would let a `\s+` in the
 * caller's pattern run across the masked formula and invent a match at its edge.
 * Only its FIRST UTF-16 unit is used — same-length is the whole point, and a
 * two-character fill would shift every index after the first formula, which is
 * exactly the class of bug this function exists to prevent.
 */
export function maskMathRuns(text: string, fill = '\u0000'): string {
  const unit = fill.slice(0, 1) || '\u0000';
  let masked = '';
  let cursor = 0;
  for (const run of locateMathRuns(text)) {
    masked += text.slice(cursor, run.start) + unit.repeat(run.end - run.start);
    cursor = run.end;
  }
  return masked + text.slice(cursor);
}

/** Does this text contain at least one real (well-formed) formula? */
export const hasMath = (text: string): boolean =>
  containsMathDelimiter(text) && splitMathSegments(text).some((s) => s.type === 'math');
